import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DataTable from 'react-data-table-component'
import { ArrowLeftIcon, DocumentTextIcon } from '@heroicons/react/20/solid'
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/16/solid'
import { Select } from 'components/select'
import { MultiSelect } from 'components/multi-select'
import { Input } from 'components/input'
import { Divider } from 'components/divider'
import { Badge } from 'components/badge'
import loadingImage from '../../assets/images/boat-loading.gif'
import Flatpickr from 'react-flatpickr'
import { useLazyQuery } from '@apollo/client'
import { GET_ROUTE_UTILIZATION_REPORT, GET_MASTER_BOAT_ROUTES } from 'graphql/reports/queries'
import moment from 'moment'
import { ErrorDialog } from 'components/custom/ResponseDialog'
import i18n from 'i18next'
import { RoutesIcon } from 'components/icons'

// Constants
const AGGREGATE_BY_OPTIONS = [
    { code: 'DAILY', label: 'Daily' },
    { code: 'WEEKLY', label: 'Weekly' },
    { code: 'MONTHLY', label: 'Monthly' },
    { code: 'YEARLY', label: 'Yearly' },
]

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50]

const INITIAL_FILTER_STATE = {
    trip_date: {
        start_date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
    },
    route_ids: [],
    aggregate_by: null,
}

export default function RouteUtilizationReport() {
    const navigate = useNavigate()
    const { t } = useTranslation()

    // States
    const [filterObj, setFilterObj] = useState(INITIAL_FILTER_STATE)
    const [data, setData] = useState([])
    const [totalRows, setTotalRows] = useState(0)
    const [perPage, setPerPage] = useState(10)
    const [currentPage, setCurrentPage] = useState(1)
    const [prevPage, setPrevPage] = useState(null)
    const [sortField, setSortField] = useState('time_period')
    const [sortDirection, setSortDirection] = useState('desc')
    const [exportLoading, setExportLoading] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    const [routeOptions, setRouteOptions] = useState([])
    const [routeLoading, setRouteLoading] = useState(false)
    const [filterApplied, setFilterApplied] = useState(false)

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setData([])
            setFilterApplied(false)
        }
    }, [])

    // GraphQL query
    const [getRouteUtilizationReport, { loading: reportLoading }] = useLazyQuery(GET_ROUTE_UTILIZATION_REPORT, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.routeUtilizationReport.success && data.routeUtilizationReport.code === '200') {
                setData(data.routeUtilizationReport.trip_utilization_report.map(formatRouteData))
                setTotalRows(data.routeUtilizationReport.total_count)
            } else {
                handleError(data.routeUtilizationReport.message)
            }
        },
        onError: error => handleError(error.message),
    })

    // Add query to fetch boat routes
    const [getMasterBoatRoutes] = useLazyQuery(GET_MASTER_BOAT_ROUTES, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.getMasterBoatRoute.success && data.getMasterBoatRoute.code === '200') {
                const options = data.getMasterBoatRoute.master_boat_routes.map(route => ({
                    value: route.master_route_id,
                    label: route.route_name.en || route.route_name.ar,
                }))
                setRouteOptions(options)
            } else {
                handleError(data.getMasterBoatRoute.message)
            }
            setRouteLoading(false)
        },
        onError: error => {
            handleError(error.message)
            setRouteLoading(false)
        },
    })

    // Fetch route options on component mount
    useEffect(() => {
        setRouteLoading(true)
        getMasterBoatRoutes({
            variables: {
                page: {
                    offset: 0,
                    limit: 100,
                },
            },
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Run once on mount - lazy queries are stable

    // Helper Functions
    const formatRouteData = route => ({
        routeId: route.route_id,
        routeName: route.route_name?.en ? route.route_name?.en : route.route_name?.ar,
        timePeriod: route.time_period,
        totalTrips: route.total_trips,
        startStation: route.start_station?.en ? route.start_station?.en : route.start_station?.ar,
        endStation: route.end_station?.en ? route.end_station?.en : route.end_station?.ar,
        totalCompletedTrips: route.total_completed_trips,
        totalCancelledTrips: route.total_cancelled_trips,
    })

    const handleError = message => {
        setDialogMessage(message || t('Failed to fetch route utilization report'))
        setShowErrDialog(true)
    }

    // Same export function logic as booking report
    const [getRouteUtilizationReportForExport] = useLazyQuery(GET_ROUTE_UTILIZATION_REPORT, {
        fetchPolicy: 'network-only',
        onError: error => {
            handleError(error.message)
            setExportLoading(false)
        },
    })

    // Modify fetchRouteUtilizationReport to receive filterData as a parameter
    const fetchRouteUtilizationReport = (page, filterData = filterObj, limit = perPage) => {
        const offset = (page - 1) * limit
        getRouteUtilizationReport({
            variables: {
                page: {
                    limit,
                    offset,
                },
                routeFilter: {
                    trip_date: {
                        start_date:
                            filterData.trip_date.start_date || moment().subtract(1, 'months').format('YYYY-MM-DD'),
                        end_date: filterData.trip_date.end_date || moment().format('YYYY-MM-DD'),
                    },
                    route_ids: filterData.route_ids?.length > 0 ? filterData.route_ids : null,
                    aggregate_by: filterData.aggregate_by || null,
                },
            },
        })
    }

    useEffect(() => {
        if (filterApplied) {
            fetchRouteUtilizationReport(currentPage, filterObj, perPage)
        }
    }, [currentPage, perPage, filterApplied])

    const handleFilter = () => {
        setCurrentPage(1)
        setPrevPage(null)
        setFilterApplied(true)
        fetchRouteUtilizationReport(1, filterObj)
    }

    const handleClear = () => {
        const resetFilter = {
            trip_date: {
                start_date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
                end_date: moment().format('YYYY-MM-DD'),
            },
            route_ids: [],
            aggregate_by: null,
        }
        setFilterObj(resetFilter)
        setCurrentPage(1)
        setPrevPage(null)
        setPerPage(10)
        setFilterApplied(false)
        setData([]) // Clear existing data
    }

    const handlePageChange = page => {
        if (filterApplied) {
            setCurrentPage(page)
        }
    }

    const handlePerRowsChange = newPerPage => {
        if (newPerPage !== perPage && filterApplied) {
            setPerPage(newPerPage)
            setCurrentPage(1)
        }
    }

    // Modify fetchBatch to receive filterData as parameter
    const fetchBatch = async (offset, limit, filterData = filterObj) => {
        try {
            const result = await getRouteUtilizationReportForExport({
                variables: {
                    page: {
                        limit,
                        offset,
                    },
                    routeFilter: {
                        trip_date: {
                            start_date:
                                filterData.trip_date.start_date || moment().subtract(1, 'months').format('YYYY-MM-DD'),
                            end_date: filterData.trip_date.end_date || moment().format('YYYY-MM-DD'),
                        },
                        route_ids: filterData.route_ids?.length > 0 ? filterData.route_ids : null,
                        aggregate_by: filterData.aggregate_by || null,
                    },
                },
            })

            if (result.data?.routeUtilizationReport?.success && result.data.routeUtilizationReport.code === '200') {
                return {
                    data: result.data.routeUtilizationReport.trip_utilization_report,
                    total: result.data.routeUtilizationReport.total_count,
                }
            } else {
                throw new Error(result.data?.routeUtilizationReport?.message || 'Failed to fetch data')
            }
        } catch (error) {
            throw error
        }
    }

    const handleExportCSV = async () => {
        try {
            setExportLoading(true)

            // Create a copy of the current filterObj state
            const currentFilterObj = { ...filterObj }

            // Get initial batch to get total count
            const initialBatch = await fetchBatch(0, 100, currentFilterObj)
            const totalRecords = initialBatch.total

            if (totalRecords === 0) {
                handleError(t('No records found to export'))
                return
            }

            let allData = [...initialBatch.data]
            let offset = 100

            // Continue fetching if there are more records
            while (allData.length < totalRecords) {
                const batchData = await fetchBatch(offset, 100, currentFilterObj)
                allData = [...allData, ...batchData.data]
                offset += 100
            }

            // Generate CSV
            const formattedData = allData.map(formatRouteData)

            const headers = [
                'Route ID',
                'Route Name',
                'Time Period',
                'Total Trips',
                'Start Station',
                'End Station',
                'Total Completed Trips',
                'Total Cancelled Trips',
            ]

            const csvContent = [
                headers.join(','),
                ...formattedData.map(row =>
                    [
                        row.routeId,
                        row.routeName,
                        row.timePeriod,
                        row.totalTrips,
                        row.startStation,
                        row.endStation,
                        row.totalCompletedTrips,
                        row.totalCancelledTrips,
                    ].join(',')
                ),
            ].join('\n')

            // Create and download file
            const blob = new Blob([csvContent], { type: 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `route-utilization-report-${moment().format('YYYY-MM-DD')}.csv`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            handleError(error.message)
        } finally {
            setExportLoading(false)
        }
    }

    // Update table columns configuration
    const columns = useMemo(
        () => [
            {
                name: t('Route Name'),
                selector: row => row.routeName,
                sortable: true,
            },
            {
                name: t('Time Period'),
                selector: row => row.timePeriod,
                sortable: true,
                sortField: 'time_period',
            },
            {
                name: t('Total Trips'),
                selector: row => row.totalTrips,
                sortable: true,
                cell: row => <span className="font-medium">{row.totalTrips}</span>,
            },
            {
                name: t('Start Station'),
                selector: row => row.startStation,
                cell: row => row.startStation,
            },
            {
                name: t('End Station'),
                selector: row => row.endStation,
                cell: row => row.endStation,
            },
            {
                name: t('Total Completed Trips'),
                selector: row => row.totalCompletedTrips,
                sortable: true,
                cell: row => <Badge color="green">{row.totalCompletedTrips}</Badge>,
            },
            {
                name: t('Total Cancelled Trips'),
                selector: row => row.totalCancelledTrips,
                sortable: true,
                cell: row => <Badge color="red">{row.totalCancelledTrips}</Badge>,
            },
        ],
        [t]
    )

    // Custom table styles with auto width and RTL support
    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#f9fafb',
                color: '#111827',
                fontWeight: 600,
                fontSize: '0.875rem',
                paddingLeft: '1rem',
                paddingRight: '1rem',
                minWidth: 'auto', // Let columns determine their width
                textAlign: i18n.language === 'ar' ? 'right' : 'left',
            },
        },
        cells: {
            style: {
                paddingLeft: '1rem',
                paddingRight: '1rem',
                minWidth: 'auto', // Let columns determine their width
                textAlign: i18n.language === 'ar' ? 'right' : 'left',
            },
        },
        pagination: {
            style: {
                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
            },
            pageButtonsStyle: {
                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                marginLeft: i18n.language === 'ar' ? '0' : '0.5rem',
                marginRight: i18n.language === 'ar' ? '0.5rem' : '0',
            },
        },
        noData: {
            style: {
                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
            },
        },
    }

    // Update loading state to use GraphQL loading
    const isLoading = reportLoading

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1]">
                            <RoutesIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                        </div>
                        <div>
                            <span className="ms-3">{t('Route Utilization Report')}</span>
                            <p className="ms-3 text-sm font-normal leading-6 text-gray-500">
                                {t('See how each route is being used')}
                            </p>
                        </div>
                    </h1>

                    {/* Filters */}
                    <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-5">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 ">{t('Date Range')}</label>
                            <Flatpickr
                                options={{
                                    mode: 'range',
                                    dateFormat: 'Y-m-d',
                                    defaultDate: [
                                        moment().subtract(1, 'months').format('YYYY-MM-DD'),
                                        moment().format('YYYY-MM-DD'),
                                    ],
                                }}
                                value={[filterObj.trip_date.start_date, filterObj.trip_date.end_date]}
                                onChange={dates => {
                                    setFilterObj({
                                        ...filterObj,
                                        trip_date: {
                                            start_date: dates[0] ? moment(dates[0]).format('YYYY-MM-DD') : null,
                                            end_date: dates[1] ? moment(dates[1]).format('YYYY-MM-DD') : null,
                                        },
                                    })
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-0">{t('Route')}</label>
                            {!routeLoading ? (
                                <MultiSelect
                                    placeholder={t('All')}
                                    value={filterObj.route_ids || []}
                                    onChange={(values) =>
                                        setFilterObj({
                                            ...filterObj,
                                            route_ids: values,
                                        })
                                    }
                                    options={routeOptions}
                                    disabled={routeLoading}
                                />
                            ) : (
                                <p className="text-sm text-gray-500 mt-1">{t('Loading routes...')}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Aggregate By')}</label>
                            <Select
                                value={filterObj.aggregate_by || ''}
                                onChange={e =>
                                    setFilterObj({
                                        ...filterObj,
                                        aggregate_by: e.target.value,
                                    })
                                }
                            >
                                <option value="">{t('All')}</option>
                                {AGGREGATE_BY_OPTIONS.map(option => (
                                    <option key={option.code} value={option.code}>
                                        {t(option.label)}
                                    </option>
                                ))}
                            </Select>
                        </div>

                        {/* Add filter buttons to grid */}
                        <div className="sm:col-span-2 flex items-end gap-2">
                            <button
                                onClick={handleFilter}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Filter')}
                            </button>
                            <button
                                onClick={handleClear}
                                className="inline-flex items-center justify-center rounded-md bg-white px-6 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <Divider className="my-4 mb-6" />

            {isLoading ? (
                <div className="flex flex-col justify-center items-center bg-white h-96">
                    <img alt="loading" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait...')}</p>
                </div>
            ) : (
                <>
                    <div className="flex justify-between items-center mb-4 -mt-3">
                        <p className="font-semibold">Route Utilization Reports</p>
                        {data.length > 0 && (
                            <button
                                onClick={handleExportCSV}
                                disabled={exportLoading}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {exportLoading ? (
                                    <>
                                        <ArrowPathIcon className="-ml-0.5 mr-1.5 h-5 w-5 animate-spin" />
                                        {t('Exporting...')}
                                    </>
                                ) : (
                                    <>
                                        <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                                        {t('Export to CSV')}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                    <DataTable
                        columns={columns}
                        data={data}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onChangePage={handlePageChange}
                        paginationDefaultPage={currentPage}
                        sortServer
                        onSort={(column, direction) => {
                            setSortField(column.sortField)
                            setSortDirection(direction)
                        }}
                        customStyles={customStyles}
                        paginationRowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                        paginationPerPage={perPage}
                        noDataComponent={
                            <div className="text-center py-24 bg-gray-50 mt-0 rounded-lg w-full">
                                <svg
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                    className="mx-auto h-12 w-12 text-gray-400"
                                >
                                    <path
                                        d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                                        strokeWidth={2}
                                        vectorEffect="non-scaling-stroke"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No records found!')}</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {t('Please add or modify your search criteria and try again.')}
                                </p>
                                <div className="mt-6"></div>
                            </div>
                        }
                    />
                </>
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={dialogMessage}
                    onClose={() => setShowErrDialog(false)}
                />
            )}
        </>
    )
}
